import React from 'react'

import { Box, CircularProgress, Grid, Paper, Button, styled } from '@mui/material'
import { CloudDownload as CloudDownloadIcon } from '@mui/icons-material'
import { env } from '@edge/env'
import ErrorLoadingReport from './ErrorLoadingReport'
import { useJasperReport } from './useJasperReport'
import { useSearchParams } from '@edge/spark/utils'
import ReportPaginationControls from './ReportPaginationControls'

const StyledDownloadDiv = styled('div')`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
`

const StyledButton = styled(Button)`
    margin: 16px 16px 16px 16px;
    ${({ theme }) => theme.breakpoints.down('md')} {
        margin: 16px 0 0 0;
    }
`

const StyledReport = styled(Paper)`
    [data-tableuuid='3352ec26-e482-4f76-b3eb-786ed8be086f'] {
        line-height: 0;
    }
    margin: 10px;
    width: 625px;
    height: 825px;
    overflow: hidden;
    margin: auto;
    ${({ theme }) => theme.breakpoints.down('md')} {
        width: 300px;
        height: 400px;
    }
`

const ErrorContainer = styled('div')`
    margin: 16px;
    width: 600px;
    height: 600px;
    ${({ theme }) => theme.breakpoints.down('md')} {
        width: 300px;
        height: 400px;
    }
`

const StyledPaper = styled(Paper)`
    width: 100%;
    margin: 15px;
`

const StyledContainerDiv = styled('div')`
    margin-bottom: 16px;
    overflow: hidden;
`

const CenterContentContainer = styled('div')`
    margin-top: 16px;
    height: 0;
    position: relative;
    top: 80px;
    text-align: center;
`

interface Props {
    execReportFlag?: boolean
    reportUrl?: string
}

const IframeGrid = styled(Grid)`
    overflow: scroll;
    height: 735px;
    ${({ theme }) => theme.breakpoints.down('md')} {
        height: 575px;
    }
`

const StyledIframe = styled('iframe')`
    width: 100%;
    height: 700px;
    border: none;
    ${({ theme }) => theme.breakpoints.down('md')} {
        height: 550px;
    }
`

interface IframeProps {
    reportUrl: Props['reportUrl']
}

const baseUrl = env.REACT_APP_REPORT_BASE_URL

const Iframe: React.FC<IframeProps> = ({ reportUrl }: IframeProps) => {
    const { report } = useSearchParams()
    const reportPath = baseUrl && reportUrl ? `${baseUrl}/${reportUrl}` : undefined

    return <StyledIframe id="iframe" title={report} src={reportPath} />
}

export const JasperReportView: React.FC<Props> = ({ execReportFlag, reportUrl }: Props) => {
    const { helper, error, type, loading } = useJasperReport()

    const [downloadLoading, setDownloadLoading] = React.useState(false)

    const downloadReport = async (evt: React.SyntheticEvent) => {
        evt.preventDefault()

        setDownloadLoading(true)
        await helper?.exportToPdf(setDownloadLoading)
    }

    if (!baseUrl) {
        throw new Error('No REPORT_BASE_URL')
    }

    function renderExecutiveView() {
        return (
            <IframeGrid container item>
                <StyledPaper>
                    <Iframe reportUrl={reportUrl} />
                </StyledPaper>
            </IframeGrid>
        )
    }

    function renderNonExecutiveView() {
        const isFullReport = type === 'full' || type === 'fullDealer'

        return (
            <>
                <StyledDownloadDiv>
                    {downloadLoading && <CircularProgress />}

                    <StyledButton
                        onClick={downloadReport}
                        disabled={isFullReport || loading || error}
                        startIcon={<CloudDownloadIcon />}
                        variant="contained"
                        color="primary"
                        sx={{ display: { xs: 'none', md: 'block' } }}
                    >
                        Download
                    </StyledButton>
                </StyledDownloadDiv>
                <StyledContainerDiv id="page">
                    <Box sx={{ width: '100%' }}>
                        {loading && <CircularProgress />}
                        <CenterContentContainer>
                            <ErrorContainer>{error && <ErrorLoadingReport message={error?.message} />}</ErrorContainer>
                        </CenterContentContainer>
                        {isFullReport ? (
                            <div id="report" />
                        ) : (
                            <>
                                <StyledReport id="report" />
                                <Box sx={{ textAlign: 'center' }}>
                                    <StyledButton
                                        onClick={downloadReport}
                                        disabled={isFullReport || loading || error}
                                        startIcon={<CloudDownloadIcon />}
                                        variant="contained"
                                        color="primary"
                                        sx={{ display: { md: 'none' } }}
                                    >
                                        Download
                                    </StyledButton>
                                    <ReportPaginationControls />
                                </Box>
                            </>
                        )}
                    </Box>
                </StyledContainerDiv>
            </>
        )
    }

    function renderLoading() {
        return (
            <CenterContentContainer>
                <CircularProgress />
            </CenterContentContainer>
        )
    }

    return loading ? renderLoading() : execReportFlag ? renderExecutiveView() : renderNonExecutiveView()
}
